import moment from "moment";
import { logout } from "./auth";

/**
 * Converts a date object to a string in ISO 8601 format.
 * @param {Date|string|number} dt - The date to convert. Can be a Date object, a string or a number that the Date constructor can handle.
 * @returns {string} The date in 'YYYY-MM-DD' format.
 */
export const toISO8601String = function (date) {
  return moment(date).format("YYYY-MM-DD");
};

/**
 * Handles the HTTP response and returns the JSON data if the response is successful.
 * Throws an error if the response is not successful.
 *
 * @param {Response} response - The HTTP response object.
 * @returns {Promise<any>} - A promise that resolves to the JSON data from the response,
 *   or null when a successful response has an empty body (e.g. an endpoint that returned null).
 * @throws {Error} - If the response is not successful, an error is thrown with the JSON data from the response.
 */
export const handleHTTPResponse = async function (response) {
  if (response.status === 401) {
    // Terminal auth failure: the session ended (24h cap or pre-shift cutoff),
    // or a renew-and-retry already failed. Recoverable expiries are handled
    // upstream in callApi, so anything reaching here warrants a login round-trip.
    logout();
    throw { status: 401 };
  }
  if (!response.ok) {
    throw await response.json();
  }
  if (response.status === 204) return;
  // NestJS sends a handler's `null` as a 200 with an empty body, which
  // response.json() can't parse. Treat it as the null it is.
  const body = await response.text();
  return body === "" ? null : JSON.parse(body);
};

/**
 * Appends the given parameters to the URL's search params.
 * @param {URL} url - The URL object to append the parameters to.
 * @param {Object} params - The parameters to append to the URL.
 * @returns {Promise<void>} - A promise that resolves when the parameters are appended.
 */
export const appendParamsToURL = async function (url, params) {
  for (let paramKey of Object.keys(params)) {
    url.searchParams.append(paramKey, params[paramKey]);
  }
};

/**
 * Indexes an array of objects by a specified property.
 * @param {string} propertyName - The property name to index by.
 * @returns {Function} - A function that takes an array of objects and returns an object indexed by the specified property.
 */
export const indexByProperty = (propertyName) => (array) => {
  return array.reduce((obj, item) => {
    obj[item[propertyName]] = item;
    return obj;
  }, {});
};
